import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import {
  deleteOne as deleteBrowseHomePage,
  duplicate as duplicateBrowseHomePage,
  fetchAll as fetchBrowseHomePages,
  saveMetadata
} from 'app/api/admin/browse/homePageApi';
import ErrorPage from 'app/components/ErrorPage';
import { MESSAGE_ACTION_GENERIC_ERROR } from 'app/constants/admin/browse';
import { BROWSE_HOME_PAGE_PATH } from 'app/constants/BrowseConstants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { LOCALE_CODE } from 'app/constants/language';
import { makeSuccessMessage } from 'app/helpers/admin/browse';
import {
  hasAdminPermissions,
  makeHomeEditPageRoute,
  makeHomeListPageRoute,
  makeHomePreviewPageRoute
} from 'app/helpers/admin/browse/home';
import useApiClient from 'app/hooks/useApiClient';
import useLocaleCodes from 'app/hooks/useLocalCodes';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import AdminBrowseHomeListPage from './AdminBrowseHomeListPage';

const AdminBrowseHomeListPageContainer = ({ localeCode }) => {
  const apiClient = useApiClient();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { availableLocaleCodes, isAvailableLocaleCode, mapLocaleCodeToLabel } = useLocaleCodes();

  const currentLibrary = useSelector(selectCurrentLibrary);
  const subdomain = currentLibrary.get('domain');

  const currentUser = useSelector(selectCurrentUser);

  const [state, setState] = useState({
    browseHomePages: [],
    browseHomePagesMetadata: {},
    isFetching: true,
    isSaving: false
  });

  const { browseHomePages, browseHomePagesMetadata, isFetching, isSaving } = state;
  const { activeBrowseHomePageId } = browseHomePagesMetadata;

  const localeDropdownOptions = Immutable.List(availableLocaleCodes).map(availableLocaleCode => {
    return Immutable.Map({
      disabled: false,
      label: mapLocaleCodeToLabel(availableLocaleCode),
      selected: availableLocaleCode === localeCode,
      value: availableLocaleCode
    });
  });

  useEffect(() => {
    setState(prevState => {
      return {
        ...prevState,
        browseHomePages: [],
        isFetching: true
      };
    });

    fetchBrowseHomePages(apiClient, localeCode)
      .then(({ browseHomePages: fetchedBrowseHomePages, metadata }) => {
        setState(prevState => {
          return {
            ...prevState,
            browseHomePages: fetchedBrowseHomePages,
            browseHomePagesMetadata: metadata
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isFetching: false
          };
        });
      });
  }, [apiClient, dispatch, localeCode, setState, subdomain]);

  const handleActivateBrowseHomePage = newActiveBrowseHomePageId => {
    const newBrowseHomePagesMetadata = {
      activeBrowseHomePageId: newActiveBrowseHomePageId,
      localeCode,
      subdomain
    };

    setState(prevState => {
      return {
        ...prevState,
        isSaving: true
      };
    });

    saveMetadata(apiClient, newBrowseHomePagesMetadata)
      .then(savedBrowseHomePagesMetadata => {
        dispatch(makeSuccessMessage('Home page activated'));

        setState(prevState => {
          return {
            ...prevState,
            browseHomePagesMetadata: savedBrowseHomePagesMetadata
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  const handleDeleteBrowseHomePage = broseHomePageId => {
    setState(prevState => {
      return {
        ...prevState,
        isSaving: true
      };
    });

    deleteBrowseHomePage(apiClient, localeCode, broseHomePageId)
      .then(() => {
        dispatch(makeSuccessMessage('Home page deleted'));

        const newBrowseHomePages = browseHomePages.filter(browseHomePage => {
          return browseHomePage.browseHomePageId !== broseHomePageId;
        });

        setState(prevState => {
          return {
            ...prevState,
            browseHomePages: newBrowseHomePages
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  const handleDuplicateBrowseHomePage = browseHomePageId => {
    setState(prevState => {
      return {
        ...prevState,
        isSaving: true
      };
    });

    duplicateBrowseHomePage(apiClient, localeCode, browseHomePageId)
      .then(duplicatedBrowseHomePage => {
        const { browseHomePageId: newBrowseHomePageId } = duplicatedBrowseHomePage;

        navigate(makeHomeEditPageRoute(localeCode, newBrowseHomePageId));
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  const handleLocaleCodeChange = newLocaleCode => {
    navigate(makeHomeListPageRoute(newLocaleCode));
  };

  const handlePreview = browseHomePageId => {
    const previewPageUrl = makeHomePreviewPageRoute(localeCode, browseHomePageId);

    window.open(previewPageUrl, '_blank');
  };

  const handleViewActiveHomePage = () => {
    window.open(BROWSE_HOME_PAGE_PATH, '_blank');
  };

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  if (!isAvailableLocaleCode(localeCode)) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  return (
    <AdminBrowseHomeListPage
      activeBrowseHomePageId={activeBrowseHomePageId}
      browseHomePages={browseHomePages}
      isFetching={isFetching}
      isSaving={isSaving}
      localeCode={localeCode}
      localeDropdownOptions={localeDropdownOptions}
      mapLocaleCodeToLabel={mapLocaleCodeToLabel}
      onActivate={handleActivateBrowseHomePage}
      onDelete={handleDeleteBrowseHomePage}
      onDuplicate={handleDuplicateBrowseHomePage}
      onLocaleCodeChange={handleLocaleCodeChange}
      onPreview={handlePreview}
      onViewActiveHomePage={handleViewActiveHomePage}
    />
  );
};

AdminBrowseHomeListPageContainer.propTypes = {
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired
};

export default AdminBrowseHomeListPageContainer;
